import { Injectable } from '@angular/core';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Observable, from } from 'rxjs';
import { IModal, IModalData, MODAL_COLORS, MODAL_MESSAGES } from './modal.interface';
import ModalComponent from './modal/modal.component';
import SpinnerComponent from './spinner/spinner.component';
import { BaseModalComponent } from './base-modal/base-modal.component';

@Injectable({
  providedIn: 'root'
})
export class ModalService {

  spinnerRef?: NgbModalRef;
  private counter = 0;

  constructor(
    private ngbModal: NgbModal,
  ) { }

  /**
    * Muestra un modal dado un componente y el objeto que contiene la informacion a mostrar en el modal
    * @param modalInfo objeto que contiene la inforamacion a mostrar en el modal
    * @param component componente que se muestra dentro del modal
    */
  private openModal(
    content: string = MODAL_MESSAGES.exitoOperacion,
    title: string = MODAL_MESSAGES.EXITO,
    icon?: string,
    color?: string,
    list: string[] | null = null
  ): void {
    const modalRef = this.ngbModal.open(ModalComponent);
    modalRef.componentInstance.modalData = {
      title,
      content,
      color,
      confirmButtonText: MODAL_MESSAGES.ACEPTAR,
      icon,
      list
    };
  }

  /**
 * Abre cualquier componente como un modal.
 * 
 * @param component Componente que se muestra dentro del modal.
 * @param modalData Datos que se pasarán al modal: 
 *   - `title`: Título del modal.
 *   - `size`: Tamaño del modal.
 *   - `data`: Datos adicionales que se pueden enviar al componente.
 *   - `showButtons`: Determina si se muestran los botones predeterminados.
 * 
 * Si `modalData` contiene `data: {...}`, estos valores serán recibidos por 
 * los `@Input()` del componente cargado en el modal.
 * Para capturar datos desde el componente se crea un Output() con el nombre "outData" y se emite en cada cambio
 */
  loadComponent(component: any, modalData?: IModalData) {
    let ngbModalOptions: NgbModalOptions = {
      size: modalData?.size || 'lg',
      scrollable: true,
      backdrop: 'static',
      // keyboard: false
    };
    const modalRef = this.ngbModal.open(BaseModalComponent, ngbModalOptions);
    modalRef.componentInstance.modalData = modalData;
    modalRef.componentInstance.setComponent(component);
    return from(modalRef.result);
  }

  /** Muestra un modal para confirmar si se continua una operación.
   * Requiere suscribirse para obtener el resultado
   * @content mensaje a mostrar en el modal
   * @returns true o false
   */
  private confirmModal(content: string, title: string, color: string, icon: string): Observable<any> {
    let ngbModalOptions: NgbModalOptions = {
      backdrop: 'static',
      keyboard: false
    };
    const modalData: IModal = {
      title,
      content,
      color,
      cancelButtonText: MODAL_MESSAGES.CANCELAR,
      confirmButtonText: MODAL_MESSAGES.CONTINUAR,
      icon
    };
    const modalRef = this.ngbModal.open(ModalComponent, ngbModalOptions);
    modalRef.componentInstance.modalData = modalData;
    return from(modalRef.result);
  }


  /**
   * Muestra un modal de confirmación con estilo de éxito (color primario y check icon).
   * Retorna la referencia del modal para manejar la confirmación.
   */
  confirmPrimary(content: string, title: string = MODAL_MESSAGES.CONFIRMAR) {
    return this.confirmModal(content, title, MODAL_COLORS.PRIMARY, 'bi-check-circle');
  }

  /**
   * Muestra un modal de confirmación con estilo de advertencia o peligro.
   * Retorna la referencia del modal para manejar la confirmación.
   */
  confirmDanger(content: string, title: string = MODAL_MESSAGES.CONFIRMAR) {
    return this.confirmModal(content, title, MODAL_COLORS.DANGER, 'bi-exclamation-circle');
  }

  /**
   * Abre un modal de tipo "primario" con el contenido, título e ícono especificados.
   */
  primaryModal(content?: string, title?: string, icon: string = 'bi-check-circle'): void {
    this.openModal(content, title, icon, MODAL_COLORS.PRIMARY);
  }

  /**
   * Abre un modal de tipo "éxito" con el contenido, título e ícono especificados.
   */
  successModal(content?: string, title?: string, icon: string = 'bi-check-circle'): void {
    this.openModal(content, title, icon, MODAL_COLORS.SUCCESS);
  }

  /**
   * Abre un modal de tipo "error" con un mensaje predeterminado de error.
   */
  errorModal(content: string = MODAL_MESSAGES.errorOperacion, title: string = MODAL_MESSAGES.ERROR, icon: string = 'bi-x-circle'): void {
    this.openModal(content, title, icon, MODAL_COLORS.DANGER);
  }

  errorModalList(content: string, list: string[]): void {
    this.openModal(content, MODAL_MESSAGES.ERROR, 'bi-x-circle', MODAL_COLORS.DANGER, list);
  }

  showSpinner(text: string = ''): void {
    this.counter++;
    if (!this.spinnerRef) {
      this.spinnerRef = this.ngbModal.open(SpinnerComponent, { centered: true, backdrop: 'static', keyboard: false, modalDialogClass: 'modal-transparent' });
      this.spinnerRef.componentInstance.text = text;
    } else {
      this.spinnerRef.componentInstance.text = text;
    }
  }

  hideSpinner(): void {
    if (this.counter > 0) {
      this.counter--;
    }
    if (this.counter === 0 && this.spinnerRef) {
      this.spinnerRef.close();
      this.spinnerRef = undefined;
    }
  }

  hideAll(): void {
    this.counter = 0;
    if (this.spinnerRef) {
      this.spinnerRef.close();
      this.spinnerRef = undefined;
    }
  }

}
